import { LinkIcon } from '@heroicons/react/20/solid';
import { ClockIcon } from '@heroicons/react/24/outline';
import { useMemo } from 'react';

import { format } from 'date-fns';
import useProductUpdateInputModal from 'src/features/product/product-detail/hooks/useProductUpdateInputModal';
import MarkdownContent from 'src/features/product/product-detail/ui/updates-tab/markdown-content/MarkdownContent';
import isFileImage from 'src/features/product/product-detail/utils/isFileImage';
import {
  AddressBlock,
  AppImage,
  ExpectedTag,
  FilesTab
} from 'src/shared/components';
import { CTA_SUPPORT } from 'src/shared/constants/update';
import { ExampleItem } from 'src/shared/types/exampleItem';

import ImageGallery from 'src/shared/components/ImageGallery';
import {
  Chip,
  Divider,
  HeroModal as Modal,
  ModalContent
} from 'src/shared/components/ui';
import { ButtonV2, Typography } from 'src/shared/components/ui';

import getExampleItemCreator from '../../utils/getExampleItemCreator';
import getExampleItemStageLabel from '../../utils/getExampleItemStageLabel';
import getShipmentProductPhotos from '../../utils/getShipmentProductPhotos';

interface SamplingExampleItemModalProps {
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  exampleItem: ExampleItem | null;
}

const LabelValueRow = ({
  label,
  value
}: {
  label: string;
  value: React.ReactNode;
}) => {
  if (value === null) {
    return null;
  }

  return (
    <div className="flex items-center gap-5">
      <Typography
        variant="body-1"
        weight="medium"
        className="text-secondary-text min-w-20 break-all"
      >
        {label}
      </Typography>
      {value}
    </div>
  );
};

const SamplingExampleItemModal: React.FC<SamplingExampleItemModalProps> = ({
  isOpen,
  onOpenChange,
  exampleItem
}) => {
  const { setData: setUpdateInputData } = useProductUpdateInputModal();

  const exampleName = exampleItem?.name || '';

  // The example item's own files, then those of the shipment that carried it.
  const files = [
    ...(exampleItem?.uploaded_files ?? []),
    ...(exampleItem?.latest_user_shipping?.external_files ?? [])
  ].filter((file) => !file.tags.includes('visibility:hidden'));

  // The example item's own photos, then the shipment's files marked as
  // product photos.
  const images = useMemo(
    () =>
      [
        ...(exampleItem?.uploaded_files?.filter(isFileImage) ?? []),
        ...getShipmentProductPhotos(
          exampleItem?.latest_user_shipping?.external_files
        )
      ].map((file) => ({
        url: file.url,
        description: file.description,
        name: file.name
      })),
    [exampleItem]
  );

  return (
    <Modal
      hideCloseButton
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      className="h-[90%] max-w-[960px]"
    >
      <ModalContent className="overflow-y-auto">
        <div className="flex flex-col p-5">
          <div className="md:grid flex flex-col-reverse md:grid-cols-[1.2fr_1fr] gap-6">
            <div className="flex flex-col">
              {images.length > 0 ? (
                <ImageGallery
                  title={exampleItem?.name}
                  imageUrls={images}
                  isCarousel
                />
              ) : (
                <div className="flex flex-col gap-2">
                  <div className="relative w-full h-[440px] bg-gray-2 rounded-2xl flex items-center justify-center overflow-hidden">
                    <div className="absolute inset-0 w-full h-full flex flex-col items-center justify-center z-20 gap-1">
                      <ClockIcon className="size-7 text-white" />
                      <Typography
                        variant="body-1"
                        weight="medium"
                        className="text-white"
                      >
                        Image pending
                      </Typography>
                    </div>
                    <AppImage id={exampleItem?.id} alt="No image" />
                  </div>
                  <div className="min-w-14 min-h-14 max-w-14 max-h-14 bg-gray-2 rounded-2xl flex items-center justify-center">
                    <AppImage
                      id={exampleItem?.id}
                      alt="No image"
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>
              )}
            </div>

            <div className="flex flex-col gap-5">
              <div className="flex items-center justify-between gap-4">
                <div className="flex flex-col gap-1">
                  <Chip color="default" className="bg-gray-2 rounded-lg">
                    <Typography
                      variant="body-3"
                      weight="medium"
                      className="text-secondary-text"
                    >
                      Name: {exampleItem?.name}
                    </Typography>
                  </Chip>

                  <div className="group flex items-center gap-2">
                    <Typography variant="h4" weight="bold">
                      {exampleItem?.name}
                    </Typography>
                  </div>
                </div>
              </div>

              <div className="flex flex-col gap-4">
                <LabelValueRow
                  label="Type"
                  value={
                    <Chip className="bg-white border border-gray-4 rounded-md font-medium capitalize">
                      {exampleItem?.example_item_type}
                    </Chip>
                  }
                />
                <LabelValueRow
                  label="Creator"
                  value={getExampleItemCreator(exampleItem)}
                />

                <LabelValueRow
                  label="Stage"
                  value={getExampleItemStageLabel(exampleItem?.stage)}
                />
                {exampleItem?.requested_at && (
                  <LabelValueRow
                    label="Requested"
                    value={format(exampleItem?.requested_at, 'MMM dd, yyyy')}
                  />
                )}
                {exampleItem?.latest_user_shipping?.delivery?.delivery_date && (
                  <LabelValueRow
                    label="Delivery"
                    value={
                      <div className="flex items-center gap-1">
                        {format(
                          exampleItem?.latest_user_shipping?.delivery
                            ?.delivery_date,
                          'MMM dd, yyyy'
                        )}
                        <ExpectedTag />
                      </div>
                    }
                  />
                )}
                {exampleItem?.latest_user_shipping?.tracking_url && (
                  <LabelValueRow
                    label="Tracking"
                    value={
                      <div
                        onClick={() => {
                          window.open(
                            exampleItem?.latest_user_shipping?.tracking_url ??
                              undefined,
                            '_blank'
                          );
                        }}
                        className="flex items-center gap-1 cursor-pointer hover:opacity-50 transition-opacity"
                      >
                        <LinkIcon className="size-4 pointer-events-none text-primary" />
                        <Typography className="text-primary">
                          Tracking link
                        </Typography>
                      </div>
                    }
                  />
                )}
              </div>

              <div>
                <div className="flex gap-2 items-center">
                  <ButtonV2
                    onPress={() => {
                      if (exampleItem) {
                        const data = {
                          name: exampleName,
                          supplierCloak: exampleItem?.producer_supplier_cloak
                        };

                        setUpdateInputData(data, CTA_SUPPORT);
                      }
                    }}
                    color="secondary"
                  >
                    Support
                  </ButtonV2>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div>
          <Divider className="w-full h-[1px] m-0 relative top-[-4px]" />

          <div className="px-5 py-7 border-b border-gray-4 flex flex-col gap-5">
            <Typography variant="h5" weight="bold">
              Notes
            </Typography>

            <div className="flex flex-col">
              <Typography
                variant="body-1"
                weight="medium"
                className="text-secondary-text"
              >
                Description
              </Typography>
              {exampleItem?.description && (
                <MarkdownContent markdownString={exampleItem?.description} />
              )}
            </div>

            <div className="flex flex-col">
              <Typography
                variant="body-1"
                weight="medium"
                className="text-secondary-text"
              >
                Notes from the customer
              </Typography>
              {exampleItem?.customer_notes && (
                <MarkdownContent markdownString={exampleItem?.customer_notes} />
              )}
            </div>
          </div>

          {exampleItem?.latest_user_shipping?.address && (
            <div className="px-5 py-7 border-b border-gray-4 flex flex-col gap-5">
              <Typography variant="h5" weight="bold">
                Shipping information
              </Typography>

              <div className="flex flex-col">
                <Typography
                  variant="body-1"
                  weight="medium"
                  className="text-secondary-text"
                >
                  Shipping address
                </Typography>

                <AddressBlock
                  address={exampleItem?.latest_user_shipping?.address}
                />
              </div>
            </div>
          )}

          {files.length > 0 && (
            <div className="pt-7 flex flex-col gap-3">
              <Typography variant="h5" weight="bold" className="px-5">
                Files
              </Typography>
              <FilesTab files={files} />
            </div>
          )}
        </div>
      </ModalContent>
    </Modal>
  );
};

export default SamplingExampleItemModal;
